import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy, UrlSegment } from '@angular/router';
import { WebComponentRegistryService } from '../web-component-bridge/web-component-registry.service';

@Injectable()
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  private static PRIMARY_ROUTER_OUTLET = 'primary';
  private storedRoutes = new Map<string, DetachedRouteHandle>();

  /**
   * Extracts the root path and the route path form the _urlSegment
   * @param route ActivatedRouteSnapshot
   * @private
   */
  private static getPathsFromRoute(route: ActivatedRouteSnapshot): string[] {
    const urlSegments = (route as any)._urlSegment.segments as UrlSegment[];
    return urlSegments.reduce((acc: string[], segment: UrlSegment) => {
      acc.push(segment.path);
      return acc;
    }, []);
  }

  /**
   * Generates the unique identifier that consists of root path and the route
   * @param route ActivatedRouteSnapshot
   * @private
   */
  private static generateIDFromRoute(route: ActivatedRouteSnapshot): string {
    return CustomRouteReuseStrategy.getPathsFromRoute(route).join('-');
  }

  /**
   * Check if the route is on the lowest (component) level
   * @param route ActivatedRouteSnapshot
   * @private
   */
  private static isLowestLevelRoute(route: ActivatedRouteSnapshot): boolean {
    return !!route.parent && !route.children.length && route.outlet === CustomRouteReuseStrategy.PRIMARY_ROUTER_OUTLET;
  }

  /**
   * Determines if the route should detach (not to be destroyed)
   * @param route ActivatedRouteSnapshot
   */
  public shouldDetach(route: ActivatedRouteSnapshot): boolean {
    // initially removes the DetachedRouteHandle from the Map
    this.storedRoutes.delete(CustomRouteReuseStrategy.generateIDFromRoute(route));
    // only lowest level routes should be detached since those don't have children
    if (CustomRouteReuseStrategy.isLowestLevelRoute(route)) {
      const paths = CustomRouteReuseStrategy.getPathsFromRoute(route);
      let loading;
      const elem = document.querySelector(`cf-wc-bridge[data-path="${paths[0]}"][route="${paths[1]}"]`);
      if (elem) {
        loading = elem.getAttribute(WebComponentRegistryService.WC_BRIDGE_DATA_LOADING_ATTR);
      }
      // only currently present, fully loaded, cf-wc-bridge should be detached
      return !!elem && !loading;
    }
    return false;
  }

  /**
   * Stores the detached route to the Map
   * @param route ActivatedRouteSnapshot
   * @param detachedTree DetachedRouteHandle
   */
  public store(route: ActivatedRouteSnapshot, detachedTree: DetachedRouteHandle): void {
    if (detachedTree) {
      this.storedRoutes.set(CustomRouteReuseStrategy.generateIDFromRoute(route), detachedTree);
    }
  }

  /**
   * Determines if the route should be re-attached
   * @param route ActivatedRouteSnapshot
   */
  public shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return (
      CustomRouteReuseStrategy.isLowestLevelRoute(route) && !!this.storedRoutes.get(CustomRouteReuseStrategy.generateIDFromRoute(route))
    );
  }

  /**
   * Retrieves the detached route from the Map
   * @param route ActivatedRouteSnapshot
   */
  public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!CustomRouteReuseStrategy.isLowestLevelRoute(route)) {
      return null;
    }
    return this.storedRoutes.get(CustomRouteReuseStrategy.generateIDFromRoute(route)) || null;
  }

  /**
   * Determines if the route should be reused
   * @param future Future ActivatedRouteSnapshot
   * @param curr Current ActivatedRouteSnapshot
   */
  public shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }
}
